import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, Clock, CheckCircle, XCircle, Send, Loader2, ChevronRight,
} from "lucide-react";
import { motion } from "framer-motion";

const STATUS_CONFIG = {
  pending: { label: "Pending Review", icon: Clock, bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-400" },
  approved: { label: "Approved", icon: CheckCircle, bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-400" },
  rejected: { label: "Rejected", icon: XCircle, bg: "bg-red-50", border: "border-red-200", text: "text-red-600", dot: "bg-red-400" },
};

export default function MySubmissionsPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/submissions`);
        setSubmissions(res.data || []);
      } catch { }
      finally { setLoading(false); }
    })();
  }, []);

  const counts = {
    pending: submissions.filter(s => s.status === "pending").length,
    approved: submissions.filter(s => s.status === "approved").length,
    rejected: submissions.filter(s => s.status === "rejected").length,
  };

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="my-submissions-page">
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="submissions-back-btn">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="text-base font-bold text-slate-800">My Submissions</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>
      ) : (
        <div className="px-4 pt-4 space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-3 gap-2" data-testid="submissions-summary">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <div key={key} className={`${cfg.bg} ${cfg.border} border rounded-xl p-3 text-center`}>
                <p className={`text-lg font-bold ${cfg.text}`}>{counts[key]}</p>
                <p className="text-[10px] text-slate-500 font-medium">{cfg.label}</p>
              </div>
            ))}
          </div>

          {submissions.length === 0 ? (
            <div className="text-center py-12">
              <Send className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-slate-500">No submissions yet</p>
              <p className="text-xs text-slate-400 mt-1">Submit a deal to earn 30 points!</p>
              <button
                onClick={() => navigate("/submit-offer")}
                className="mt-4 px-4 py-2 text-sm font-semibold bg-slate-800 text-white rounded-xl hover:bg-slate-700"
                data-testid="go-submit-btn"
              >
                Submit a Deal
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {submissions.map((sub, idx) => {
                const cfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
                const Icon = cfg.icon;
                return (
                  <motion.div
                    key={sub.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.04 }}
                    className="bg-white rounded-xl border border-slate-100 shadow-sm p-3.5"
                    data-testid={`submission-card-${sub.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Icon className={`w-4 h-4 ${cfg.text}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800 truncate">{sub.business_name}</p>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                            {cfg.label}
                          </span>
                        </div>
                        {sub.offer_description && (
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{sub.offer_description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-1.5">
                          {sub.category && <span className="text-[10px] text-slate-400">{sub.category}</span>}
                          {sub.location && <span className="text-[10px] text-slate-400">{sub.location}</span>}
                          {sub.matched_business_id && (
                            <span className="text-[10px] text-sky-500 font-medium">Linked to existing</span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-300 mt-1">
                          {new Date(sub.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}
      <BottomNav />
    </div>
  );
}
